import React, { useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Chip,
  useTheme,
  LinearProgress,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  Event as EventIcon,
  LocalHospital as HospitalIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  MedicalServices as MedicalServicesIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import { useTenant } from '../contexts/TenantContext';
import { useResidents } from '../hooks/useResidents';
import { useAssessments } from '../hooks/useAssessments';
import { useBilling } from '../hooks/useBilling';
import { useNotifications } from '../hooks/useNotifications';

const COLORS = ['#2CA6A4', '#174A5A', '#FF6B6B', '#4ECDC4'];

const DashboardPage: React.FC = () => {
  const theme = useTheme();
  const { tenant } = useTenant();

  // Fetch data from real APIs
  const {
    residents,
    loading: residentsLoading,
    error: residentsError,
    totalElements: totalResidents,
    fetchResidents,
  } = useResidents();

  const {
    assessments,
    loading: assessmentsLoading,
    fetchAssessments,
  } = useAssessments();

  const {
    billingClaims,
    loading: billingLoading,
    fetchBillingClaims,
  } = useBilling();

  const {
    notifications,
    unreadCount,
    loading: notificationsLoading,
  } = useNotifications({ userId: '' });

  // Load data on component mount
  useEffect(() => {
    fetchResidents(0, 100); // Get first 100 residents for stats
    fetchAssessments();
    fetchBillingClaims(0); // Fetch first page of billing claims
  }, []);

  // Calculate statistics from real data
  const activeResidents = residents.filter((r) => r.status === 'ACTIVE').length;
  const pendingAssessments = assessments.filter((a) => a.status === 'IN_PROGRESS' || a.status === 'DRAFT').length;
  const completedAssessments = assessments.filter((a) => a.status === 'COMPLETED' || a.status === 'APPROVED').length;
  const pendingClaims = billingClaims.filter((c) => c.status === 'SUBMITTED' || c.status === 'IN_REVIEW').length;

  // Status distribution for pie chart
  const statusData = [
    {
      name: 'Active',
      value: residents.filter((r) => r.status === 'ACTIVE').length,
    },
    {
      name: 'Discharged',
      value: residents.filter((r) => r.status === 'DISCHARGED').length,
    },
    {
      name: 'On Leave',
      value: residents.filter((r) => r.status === 'ON_LEAVE').length,
    },
    {
      name: 'Transferred',
      value: residents.filter((r) => r.status === 'TRANSFERRED').length,
    },
  ].filter((item) => item.value > 0);

  // Recent residents for activity feed
  const recentResidents = [...residents]
    .sort((a, b) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime())
    .slice(0, 5);

  const loading = residentsLoading || assessmentsLoading || billingLoading;

  if (loading && residents.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header Section */}
      <Box
        sx={{
          mb: 4,
          pb: 3,
          borderBottom: `2px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            mb: 1,
          }}
        >
          {tenant?.name || 'Dashboard'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Welcome back! Here's your facility overview.
        </Typography>
      </Box>

      {residentsError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {residentsError}
        </Alert>
      )}

      {/* Key Stats Cards */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        {/* Total Residents */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: '100%',
              background: 'linear-gradient(135deg, #2CA6A4 0%, #1B5E6A 100%)',
              color: 'white',
              boxShadow: '0 8px 24px rgba(44, 166, 164, 0.15)',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 32px rgba(44, 166, 164, 0.25)',
              },
            }}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  Total Residents
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {residentsLoading ? '...' : totalResidents}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                <PeopleIcon sx={{ fontSize: 32 }} />
              </Avatar>
            </CardContent>
          </Card>
        </Grid>

        {/* Active Residents */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: '100%',
              background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
              color: 'white',
              boxShadow: '0 8px 24px rgba(76, 175, 80, 0.15)',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 32px rgba(76, 175, 80, 0.25)',
              },
            }}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  Active Residents
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {residentsLoading ? '...' : activeResidents}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                <CheckCircleIcon sx={{ fontSize: 32 }} />
              </Avatar>
            </CardContent>
          </Card>
        </Grid>

        {/* Pending Assessments */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: '100%',
              background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
              color: 'white',
              boxShadow: '0 8px 24px rgba(255, 152, 0, 0.15)',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 32px rgba(255, 152, 0, 0.25)',
              },
            }}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  Pending Assessments
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {assessmentsLoading ? '...' : pendingAssessments}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                <AssignmentIcon sx={{ fontSize: 32 }} />
              </Avatar>
            </CardContent>
          </Card>
        </Grid>

        {/* Pending Claims */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: '100%',
              background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
              color: 'white',
              boxShadow: '0 8px 24px rgba(33, 150, 243, 0.15)',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 32px rgba(33, 150, 243, 0.25)',
              },
            }}
          >
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  Pending Claims
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {billingLoading ? '...' : pendingClaims}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                <MoneyIcon sx={{ fontSize: 32 }} />
              </Avatar>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Resident Status Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              Resident Status Distribution
            </Typography>
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                No resident data available
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              Recent Admissions
            </Typography>
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {recentResidents.length > 0 ? (
                recentResidents.map((resident, index) => (
                  <Box
                    key={resident.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      pb: 2,
                      borderBottom: index < recentResidents.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                    }}
                  >
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                      <PeopleIcon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {resident.firstName} {resident.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Room: {resident.roomNumber || 'Not Assigned'} • Admitted:{' '}
                        {new Date(resident.admissionDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Chip
                      label={resident.status}
                      size="small"
                      color={resident.status === 'ACTIVE' ? 'success' : 'default'}
                    />
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                  No recent admissions
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Assessment Status */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              Assessment Overview
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Pending
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF9800' }}>
                      {pendingAssessments}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={
                        assessments.length > 0 ? (pendingAssessments / assessments.length) * 100 : 0
                      }
                      sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: '#FFE0B2' }}
                      color="warning"
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Completed
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
                      {completedAssessments}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={
                        assessments.length > 0 ? (completedAssessments / assessments.length) * 100 : 0
                      }
                      sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: '#C8E6C9' }}
                      color="success"
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                      {assessments.length}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={100}
                      sx={{ flex: 1, height: 8, borderRadius: 4 }}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
